import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/new-ui/table';
import type { Projects } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';
import type { SearchParams } from '@/utils/parseSearchParamsToSearchQuery';

import type { Estates } from '../api/estates';
import { useEstatesScrollRestore } from '../hooks/use-estates-scroll-restore';
import type { EstateListData } from './estate-list';
import { EstatesInfiniteScroll } from './estates-infinite-scroll';
import { EstatesTableRow } from './estates-table-row';

const route = getRouteApi('/_authenticated/_sidebar-layout/estates/');

export type SortField = 'name' | 'rooms' | 'area' | 'price' | 'createdAt' | 'updatedAt' | 'creator';

export type SortState = {
  by?: SortField;
  order?: 'asc' | 'desc';
};

const COLUMN_COUNT = 7;

type SortableHeaderProps = {
  field: SortField;
  currentSort?: SortState;
  className?: string;
  onSort: (field: SortField) => void;
  children: ReactNode;
};

export const SortableHeader = ({
  field,
  currentSort,
  className,
  onSort,
  children,
}: SortableHeaderProps) => {
  const isActive = currentSort?.by === field;
  const order = isActive ? currentSort?.order : undefined;
  const ariaSort = order === 'asc' ? 'ascending' : order === 'desc' ? 'descending' : 'none';

  return (
    <TableHead
      aria-sort={ariaSort}
      className={cn('cursor-pointer select-none whitespace-nowrap hover:bg-muted/50', className)}
      onClick={() => onSort(field)}
    >
      <button
        type="button"
        className="flex items-center gap-1.5 font-medium"
        onClick={(event) => {
          event.stopPropagation();
          onSort(field);
        }}
      >
        {children}
        {order === 'asc' ? <ChevronUp className="h-4 w-4" /> : null}
        {order === 'desc' ? <ChevronDown className="h-4 w-4" /> : null}
      </button>
    </TableHead>
  );
};

/** Click order of a sortable column: ascending, descending, then back to the default order */
export const getNextSort = (
  prevSort: SortState | undefined,
  field: SortField
): { by: SortField; order: 'asc' | 'desc' } | undefined => {
  if (prevSort?.by !== field) return { by: field, order: 'asc' };
  if (prevSort.order === 'asc') return { by: field, order: 'desc' };
  if (prevSort.order === 'desc') return undefined;
  return { by: field, order: 'asc' };
};

type EstatesTableProps = {
  data: EstateListData[];
  filters?: SearchParams;
  filterOwn?: boolean;
  infiniteScroll?: boolean;
  /** While the first page loads, the empty state stays hidden */
  isPending?: boolean;
  type?: 'estate' | 'project' | 'combined';
  onCopy: (id: string) => void;
  onDelete: (id: string) => void;
};

/** List view of the estates page in the new design. */
export const EstatesTable = ({
  data,
  filters,
  filterOwn,
  infiniteScroll = true,
  isPending = false,
  type = 'estate',
  onCopy,
  onDelete,
}: EstatesTableProps) => {
  const { t } = useTranslation();
  const { sort } = route.useSearch();
  const currentSort = sort as SortState | undefined;
  const navigate = route.useNavigate();

  useEstatesScrollRestore();

  const handleSort = useCallback(
    (field: SortField) => {
      navigate({
        search: (prev) => {
          const nextSort = getNextSort(prev.sort as SortState | undefined, field);

          if (!nextSort) {
            const { sort: _, ...rest } = prev;
            return rest;
          }

          return { ...prev, sort: nextSort };
        },
      });
    },
    [navigate]
  );

  const renderRow = (estate: Estates[number] | Projects[number]) => (
    <EstatesTableRow estate={estate as EstateListData} onCopy={onCopy} onDelete={onDelete} />
  );

  const renderInfiniteScroll = (scrollType: 'estate' | 'project') => (
    <EstatesInfiniteScroll
      filters={filters}
      filterOwn={filterOwn}
      sort={sort}
      type={scrollType}
      cardRenderer={renderRow}
      variant="table-row"
      colSpan={COLUMN_COUNT}
    />
  );

  return (
    <div className="new-ui">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table className="min-w-[640px]">
          <TableHeader>
            <TableRow>
              <SortableHeader
                field="name"
                className="min-w-[260px]"
                currentSort={currentSort}
                onSort={handleSort}
              >
                {t('title')}
              </SortableHeader>
              <SortableHeader field="price" currentSort={currentSort} onSort={handleSort}>
                {t('price')}
              </SortableHeader>
              <SortableHeader
                field="area"
                className="hidden lg:table-cell"
                currentSort={currentSort}
                onSort={handleSort}
              >
                {t('area')}
              </SortableHeader>
              <SortableHeader
                field="rooms"
                className="hidden lg:table-cell"
                currentSort={currentSort}
                onSort={handleSort}
              >
                {t('rooms')}
              </SortableHeader>
              <SortableHeader
                field="creator"
                className="hidden xl:table-cell"
                currentSort={currentSort}
                onSort={handleSort}
              >
                {t('author')}
              </SortableHeader>
              <SortableHeader field="createdAt" currentSort={currentSort} onSort={handleSort}>
                {t('created')}
              </SortableHeader>
              <TableHead className="w-[112px]">
                <span className="sr-only">{t('actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!isPending && data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className="py-8 text-center text-muted-foreground"
                >
                  {t('notMatchingResults')}
                </TableCell>
              </TableRow>
            ) : null}
            {data.map((estate) => (
              <EstatesTableRow
                key={estate.id}
                estate={estate}
                onCopy={onCopy}
                onDelete={onDelete}
              />
            ))}
            {infiniteScroll ? (
              type === 'combined' ? (
                <>
                  {renderInfiniteScroll('estate')}
                  {renderInfiniteScroll('project')}
                </>
              ) : (
                renderInfiniteScroll(type)
              )
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
